import axios from '@/plugins/axios';
import { API_BASE_URL } from '@/constants';

// Thumbnails dos criativos como object URLs, UMA por criativo, vivas enquanto o
// app viver: o card da pasta e a tela do criativo mostram a MESMA imagem sem
// novo request (a tela abre com ela borrada enquanto o heatmap grande não
// chega). A versão é o `updated_at`, o mesmo cache-buster `?t=` de sempre:
// versão mais nova substitui e revoga a antiga; resposta atrasada de versão
// velha é descartada. Quem revoga é SÓ este módulo — nunca o componente.
// EN: one object URL per experiment, versioned by updated_at; newest wins and
// the module owns revocation.
const entryById = new Map();      // id -> { version, url }
const inflightByKey = new Map();  // `${id}:${version}` -> Promise<string>

export function thumbnailVersion(item) {
    const stamp = item?.updated_at || item?.created_at;
    return String(stamp ? new Date(stamp).getTime() : Date.now());
}

export function peekThumbnail(experimentId) {
    return entryById.get(experimentId)?.url || null;
}

export function fetchThumbnailCached(experimentId, version) {
    const hit = entryById.get(experimentId);
    if (hit && hit.version === version) return Promise.resolve(hit.url);

    const key = `${experimentId}:${version}`;
    if (inflightByKey.has(key)) return inflightByKey.get(key);

    const promise = axios
        .get(`${API_BASE_URL}/experiments/${experimentId}/thumbnail?t=${version}`, { responseType: 'blob' })
        .then((response) => {
            inflightByKey.delete(key);
            const url = URL.createObjectURL(response.data);
            const current = entryById.get(experimentId);
            // Resposta lenta de uma versão mais velha não pode sobrescrever a nova.
            if (current && Number(current.version) > Number(version)) {
                URL.revokeObjectURL(url);
                return current.url;
            }
            entryById.set(experimentId, { version, url });
            if (current && current.url !== url) URL.revokeObjectURL(current.url);
            return url;
        })
        .catch((error) => {
            inflightByKey.delete(key);
            throw error;
        });

    inflightByKey.set(key, promise);
    return promise;
}

export function invalidateThumbnail(experimentId) {
    const current = entryById.get(experimentId);
    if (current) {
        URL.revokeObjectURL(current.url);
        entryById.delete(experimentId);
    }
    for (const key of Array.from(inflightByKey.keys())) {
        if (key.startsWith(`${experimentId}:`)) inflightByKey.delete(key);
    }
}

export function __resetThumbnailCacheForTests() {
    for (const { url } of entryById.values()) URL.revokeObjectURL(url);
    entryById.clear();
    inflightByKey.clear();
}
